import React, { useState } from "react";
import api, { formatApiError } from "@/api";
import { X, Upload, FileText, CheckCircle2, AlertCircle } from "lucide-react";

const SAMPLE_CSV = `Ticker,Shares,Cost Basis
AAPL,50,150.00
MSFT,30,280.00
KO,100,55.00
SCHD,25,63.50`;

export default function ImportCsvModal({ open, onClose, onImported }) {
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCsvText(""); setError(""); setResult(null); setBusy(false);
  };

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setCsvText(e.target.result || "");
    reader.readAsText(file);
  };

  const onSubmit = async () => {
    if (!csvText.trim()) {
      setError("Paste CSV content or drop a file first.");
      return;
    }
    setBusy(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/holdings/import-csv", { csv_text: csvText });
      setResult(data);
      onImported();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="import-csv-modal-backdrop"
    >
      <div
        className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="import-csv-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <p className="label-xs mb-1">Broker import</p>
            <h3 className="font-serif text-2xl">Import portfolio from CSV</h3>
            <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
              Fidelity, Schwab, Robinhood, Vanguard, E*TRADE — export positions CSV and drop here.
            </p>
          </div>
          <button className="btn-ghost" onClick={() => { reset(); onClose(); }} data-testid="close-import-modal-button">
            <X size={18} />
          </button>
        </div>

        {!result && (
          <>
            {/* Dropzone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
              }}
              className="border border-dashed p-6 text-center cursor-pointer transition-colors"
              style={{
                borderColor: dragOver ? "var(--gain)" : "var(--border)",
                background: dragOver ? "var(--gain-bg)" : "transparent",
              }}
              onClick={() => document.getElementById("csv-file-input")?.click()}
              data-testid="csv-dropzone"
            >
              <Upload size={24} className="mx-auto mb-2" style={{ color: "var(--text-secondary)" }} />
              <p className="text-sm">Drop a CSV file here or click to browse</p>
              <p className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>
                Or paste CSV content in the box below
              </p>
              <input
                id="csv-file-input"
                data-testid="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>

            {/* Textarea */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="label-xs">CSV content</label>
                <button
                  data-testid="load-sample-csv-button"
                  className="btn-ghost text-[10px] underline"
                  onClick={() => setCsvText(SAMPLE_CSV)}
                >
                  load sample
                </button>
              </div>
              <textarea
                data-testid="csv-textarea"
                className="input-base font-mono !text-[11px] leading-relaxed"
                style={{ minHeight: 180, resize: "vertical" }}
                placeholder={"Ticker,Shares,Cost Basis\nAAPL,50,150.00\n..."}
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
              />
              <p className="text-[10px] mt-2" style={{ color: "var(--text-secondary)" }}>
                Required: <span className="font-semibold">Ticker/Symbol</span> and <span className="font-semibold">Shares/Quantity</span> columns.
                Optional: <span className="font-semibold">Cost Basis / Average Cost / Price Paid</span>.
              </p>
            </div>

            {error && (
              <div data-testid="import-error" className="text-xs px-3 py-2 mt-4"
                style={{ background: "var(--loss-bg)", color: "var(--loss)", border: "1px solid var(--loss)" }}>
                {error}
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button
                data-testid="import-submit-button"
                type="button" disabled={busy || !csvText.trim()}
                className="btn-primary flex-1"
                onClick={onSubmit}
              >
                {busy ? "Importing..." : "Import positions →"}
              </button>
              <button className="btn-secondary" onClick={() => { reset(); onClose(); }}>Cancel</button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="import-result">
            <div className="flex items-center gap-2 mb-4" style={{ color: "var(--gain)" }}>
              <CheckCircle2 size={20} />
              <p className="font-serif text-xl">Imported {result.imported_count} positions</p>
            </div>

            {result.detected_columns && (
              <div className="text-[10px] mb-4 space-x-3" style={{ color: "var(--text-secondary)" }}>
                <span>Ticker → <span className="text-white">{result.detected_columns.ticker}</span></span>
                <span>Shares → <span className="text-white">{result.detected_columns.shares}</span></span>
                <span>Cost → <span className="text-white">{result.detected_columns.cost_basis || "—"}</span></span>
              </div>
            )}

            {result.imported.length > 0 && (
              <div className="mb-4">
                <p className="label-xs mb-2">Imported</p>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {result.imported.map((it, i) => (
                    <div key={i} className="text-xs flex justify-between border-b py-1"
                      style={{ borderColor: "var(--border)" }}>
                      <span>
                        <span className="font-semibold">{it.ticker}</span>
                        {it.merged && <span className="ml-2 text-[9px] uppercase" style={{color: "var(--text-secondary)"}}>merged</span>}
                      </span>
                      <span style={{ color: "var(--text-secondary)" }}>
                        {it.shares} sh @ ${it.cost_basis.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.skipped.length > 0 && (
              <div className="mb-4">
                <p className="label-xs mb-2 flex items-center gap-2">
                  <AlertCircle size={12} /> Skipped {result.skipped.length} rows
                </p>
                <div className="space-y-1 max-h-24 overflow-y-auto text-[10px]" style={{ color: "var(--text-secondary)" }}>
                  {result.skipped.map((s, i) => (
                    <div key={i}>Row {s.row}: {s.reason}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                data-testid="import-done-button"
                className="btn-primary flex-1"
                onClick={() => { reset(); onClose(); }}
              >
                Done →
              </button>
              <button
                className="btn-secondary"
                onClick={() => { setResult(null); setCsvText(""); }}
              >
                Import another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
